<template>
  <div class="page-domain-search">
    <PageHeader>
      <div class="heading">
        <h1>
          <strong class="name">{{ company.name }}</strong>
          / Domain Name Search
          <span v-if="siteLoaded">
          / <strong class="name">{{ site.name }}</strong>
          </span>
        </h1>
      </div>

      <div class="header-actions">
        <router-link
          :to="{name: 'company-domains', params: {companyId: companyId}}"
          class="btn is-tertiary">
          My Domain Names
        </router-link>
      </div>
    </PageHeader>

    <div class="page-columns">
      <div class="col main">
        <div class="page-content" :class="{'is-searching': isSearching}">
          <div>
            <div
              class="domain-decorations"
              :class="{'is-active': !isSearching}">
              <transition name="fade" mode="out-in">
                <Sprite
                  :key="`industry-${activeIndustry}`"
                  :name="`industry-${activeIndustry}`"/>
              </transition>

              <h3>
                <VueTyper
                  :typeDelay="70"
                  :preTypeDelay="0"
                  caretAnimation="smooth"
                  :text="domainExamples"/>
              </h3>
            </div>

            <form class="domain-search-form" @submit.prevent="searchDomain">
              <input
                autofocus
                v-model="query"
                type="text"
                :placeholder="searchPlaceholder"
                name="domain-search"
              />
              <Btn :isLoading="searchLoading" :primary="!isSearching" :subtle="isSearching">Search</Btn>
            </form>

            <div class="domain-search-results">
              <table-data
                v-if="isSearching"
                fixedColumns
                :rows="domainResults"
                :headings="[
                  {key: 'purchase-domain-name', heading: 'Domain name'},
                  {key: 'purchase-price', heading: 'Price'},
                  {key: 'purchase-renewal-price', heading: 'Renewal Price'},
                  {key: 'purchase', heading: 'Purchase'}
                ]"
              >
                <div
                  v-for="(domain, index) in domainResults"
                  :slot="'td-' + index + '-purchase-domain-name'"
                  :key="'td-' + index + '-purchase-domain-name'"
                >
                  {{domain.domainName}}
                </div>

                <div
                  v-for="(domain, index) in domainResults"
                  :slot="'td-' + index + '-purchase-price'"
                  :key="'td-' + index + '-purchase-price'"
                >
                  {{displayPrice(domain.purchasePrice)}}
                </div>

                <div
                  v-for="(domain, index) in domainResults"
                  :slot="'td-' + index + '-purchase-renewal-price'"
                  :key="'td-' + index + '-purchase-renewal-price'"
                >
                  {{displayPrice(domain.renewalPrice)}}
                </div>

                <div
                  v-for="(domain, index) in domainResults"
                  :slot="'td-' + index + '-purchase'"
                  :key="'td-' + index + '-purchase'"
                >
                  <Btn
                    v-if="domainInCart(domain)"
                    @click="removeFromCart(domain)"
                    subtle
                  >
                    Remove
                  </Btn>

                  <Btn
                    v-else
                    @click="addToCart(domain)"
                    secondary
                  >
                    Add to cart
                  </Btn>
                </div>
              </table-data>

              <pagination @changePage="changePage" :pagination="pageData">
              </pagination>
            </div>
          </div>
        </div>
      </div>

      <div class="col side">
        <PageSummary>
          <transition name="fade" mode="out-in">
            <div v-if="hasLineItems" key="domain-cart" class="domain-cart">
              <div>
                <h1>My Cart</h1>

                <table-data
                  :rows="lineItems"
                  :headings="[
                    {key: 'cart-domain-name', heading: ''},
                    {key: 'cart-price', heading: ''},
                    {key: 'cart-remove', heading: ''}
                  ]"
                >
                  <div
                    v-for="(domain, index) in lineItems"
                    :slot="'td-' + index + '-cart-domain-name'"
                    :key="'td-' + index + '-cart-domain-name'"
                  >
                    {{ domain.domainName }}
                  </div>

                  <div
                    v-for="(domain, index) in lineItems"
                    :slot="'td-' + index + '-cart-price'"
                    :key="'td-' + index + '-cart-price'"
                  >
                    {{ displayPrice(domain.purchasePrice) }}
                  </div>

                  <div
                    v-for="(domain, index) in lineItems"
                    :slot="'td-' + index + '-cart-remove'"
                    :key="'td-' + index + '-cart-remove'"
                  >
                    <Btn
                      @click="removeFromCart(domain)"
                      subtle
                    >
                      Remove
                    </Btn>
                  </div>
                </table-data>

                <div class="btn-purchase">
                  <router-link
                    class="btn is-primary"
                    :to="{name: 'domain-contact-details'}">
                    Proceed to Checkout
                  </router-link>
                </div>
              </div>
            </div>

            <div key="domain-info" v-else>
              <div>
                <h1>Domain Names</h1>
                <p>Every domain that points to a PageMakerPro site comes with a free SSL certificate so your visitors can see that your website is secure.</p>
                <p>We take privacy seriously. We automatically provide WHOIS privacy for all eligible domains, free of charge.</p>
              </div>
            </div>
          </transition>
        </PageSummary>
      </div>
    </div>

    <router-view
      :companyName="company.name"
      :btnLoading="btnLoading"
      @contactDetails="updateContactDetails"/>
  </div>
</template>

<script>
import page from '@/mixins/page'
import pagination from '@/mixins/pagination'
import {mapState} from 'vuex'

import {VueTyper} from 'vue-typer'

import {readCompanyData} from '@/lib/companies'
import {readCompanySiteData} from '@/lib/sites'

import {
  searchDomainsData,
  createCompanyDomainsInvoiceData,
  createCompanyDomainData
} from '@/lib/domains'

import Table from '@/components/Table'
import Btn from '@/components/Btn'
import Sprite from '@/components/Sprite'
import PageHeader from '@/components/PageHeader'
import PageSummary from '@/components/PageSummary'

const DOMAIN_FEE = process.env.VUE_APP_DOMAIN_FEE

export default {
  name: 'company-domains',

  mixins: [page, pagination],

  props: {
    companyId: {
      type: String,
      required: true
    }
  },

  components: {
    TableData: Table,
    PageHeader,
    PageSummary,
    Btn,
    Sprite,
    VueTyper
  },

  data() {
    return {
      query: '',
      isSearching: false,
      searchLoading: false,
      btnLoading: false,
      siteLoaded: false,
      purchaseLoading: [],
      industries: [
        'massage',
        'barber',
        'boxing-gym',
        'builder',
        'butcher',
        'dog-training',
        'plumber',
        'tennis-club',
        'tutor'
      ],
      domainExamples: [
        'beebeautiful.com',
        'jpm-barbering.com',
        'mma-n-boxing.club',
        'ldn-building.site',
        'bills-butchers.net',
        'thehappypups.co.uk',
        'thewiseplumber.net',
        'united-tennis.club',
        'itutoring.services'
      ],
      activeIndustry: {},
      contactDetails: {},
      lineItems: [],
      title: 'Domain Name Search',
      company: {},
      site: {},
      selectedDomain: {},
      domainSearchResults: []
    }
  },

  methods: {
    getData() {
      readCompanyData(this.companyId).then(({data}) => {
        this.company = data
      })

      if (this.siteId) {
        readCompanySiteData(this.companyId, this.siteId).then(({data}) => {
          this.site = data
          this.siteLoaded = true
        })
      }
    },

    updateContactDetails(details) {
      this.contactDetails = details
      this.generateInvoice()
    },

    cycleIndustries() {
      let index = 0

      this.activeIndustry = this.industries[index]

      window.setInterval(() => {
        index++

        if (index === this.industries.length) {
          index = 0
        }

        this.activeIndustry = this.industries[index]
      }, 3400)
    },

    searchDomain() {
      this.searchLoading = true

      searchDomainsData(this.companyId, this.query).then(({data}) => {
        this.domainSearchResults = data
        this.searchLoading = false
        this.isSearching = true
      })
    },

    addToCart(domain) {
      this.lineItems.push(domain)
    },

    removeFromCart(domain) {
      const index = this.lineItems.indexOf(domain)

      if (index > -1) {
        this.lineItems.splice(index, 1)
      }
    },

    generateInvoice() {
      let invoice = {
        line_items: this.invoiceItems,
        contact_info: this.contactDetails
      }

      this.btnLoading = true

      createCompanyDomainsInvoiceData(this.companyId, invoice)
        .then(({data}) => {
          let invoiceId = data.invoice_id

          this.$router.push(
            {
              name: 'company-domain-invoice',
              params: {
                invoiceId: invoiceId
              },
              query: {
                domain: true
              }
            }
          )

          this.btnLoading = false
        })
        .catch(({response}) => {})
    },

    purchaseDomain(domain, index) {
      this.purchaseLoading[index] = true

      let data = {
        create_domain: {
          domain: {
            domainName: domain.domainName
          },
          purchasePrice: domain.purchasePrice,
          purchaseType: domain.purchaseType,
          years: 1
        }
      }

      createCompanyDomainData(this.companyId, data)
        .then(({data}) => {
          this.purchaseLoading = false
        })
        .catch(() => {
          this.purchaseLoading = false
        })
    }
  },

  computed: {
    ...mapState(['currentUser']),

    displayPrice() {
      return (price) => {
        let totalPrice = parseFloat(price + DOMAIN_FEE)
        return price ? `£${totalPrice.toFixed(2)}` : '–'
      }
    },

    siteId() {
      return this.$route.query.siteId || false
    },

    invoiceItems() {
      let items = []

      this.lineItems.forEach((item) => {
        let price = item.purchasePrice
        let totalPrice = parseFloat(price + DOMAIN_FEE).toFixed(2) * 100

        items.push(
          {
            domain_name: item.domainName,
            amount: totalPrice
          }
        )
      })

      return items
    },

    searchPlaceholder() {
      if (this.siteLoaded) {
        let name = this.site.name.replace(/\s+/g, '-').toLowerCase()
        return `e.g. ${name}.co.uk`
      } else {
        return 'e.g. your-company-name.co.uk'
      }
    },

    hasLineItems() {
      return !!this.lineItems.length
    },

    domainInCart() {
      return (domain) => {
        return this.lineItems.includes(domain)
      }
    },

    domainResults() {
      return this.domainSearchResults.results
        ? this.domainSearchResults.results.filter(
          (domain) => domain.purchasable === true
        ) : false
    }
  },

  mounted() {
    this.getData()
    this.cycleIndustries()
  }
}
</script>

<style lang="sass">
@import '../stylesheets/base/variables'
@import '../stylesheets/helpers/mixins'
@import '../stylesheets/helpers/extends'

.page-domain-search
  @extend %page

  .is-searching
    .domain-search-form
      margin-top: 0

  .domain-search-form
    text-align: center
    width: 100%
    transition: margin .3s ease-in-out
    margin: $gap-large 0 $gap-medium

    input
      padding: 10px $gap-small
      margin: 0 $gap-small
      width: 400px
      font-size: 18px

      &:focus
        outline: none

    .btn
      padding: $gap-small

  .domain-decorations
    display: none

    @include media($media-gt-large)
      display: block
      position: absolute
      top: 50px
      left: 50%
      transform: translate(-50%, -400px)
      text-align: center
      opacity: 0
      transition: all 1s ease-in-out

      &.is-active
        opacity: 0.25
        pointer-events: none
        transform: translate(-50%, 0)

    h3
      margin-top: 30px

    .sprite
      width: 200px
      height: 200px

      @include themify($themes)
        fill: themed('color-secondary')

  .domain-cart
    padding-top: $gap-medium

    h1
      margin-bottom: $gap-small

    thead
      display: none

    .btn-purchase
      text-align: center
      margin: $gap-medium auto 0

  .page-content
    padding-bottom: 0

    &.is-searching
      .domain-search-results
        max-height: 65vh
        overflow: auto
        opacity: 1

    > div
      display: flex
      flex-direction: column
      justify-content: center
      height: 100%

  .domain-search-results
    max-height: 0
    overflow: hidden
    opacity: 0
    transition: all 1s ease-in-out, opacity .5s 1s ease-in-out

    .btn
      min-width: 135px
</style>
